/**
 * @file QR 이벤트 API
 * 행사 관리(admin), 참가자 등록·조회·출석·다운로드·엑셀 업로드,
 * 공개 참가 등록 및 명함 조회(public) 등을 처리합니다.
 */

import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const qrEventsApi = {
    // 행사 (admin)
    /** 행사 목록 조회 */
    getEvents:   ()                    => api.get(`${API_BASE}/qr-events`),

    /** 행사 생성
     * @param {{ name: string, startDate: string, endDate: string }} event - 행사 정보
     */
    createEvent: (event)               => api.post(`${API_BASE}/qr-events`, event),

    /** 행사 수정
     * @param {string} eventName - 수정할 행사명 (URL 인코딩 처리됨)
     * @param {object} event - 수정할 행사 데이터
     */
    updateEvent: (eventName, event)    =>
        api.put(`${API_BASE}/qr-events/${encodeURIComponent(eventName)}`, event),

    /** 행사 삭제
     * @param {string} eventName - 삭제할 행사명 (URL 인코딩 처리됨)
     */
    deleteEvent: (eventName)           =>
        api.delete(`${API_BASE}/qr-events/${encodeURIComponent(eventName)}`),

    // 참가자 (admin)
    /** 행사 참가자 전체 목록 조회
     * @param {string} eventName - 행사명
     */
    getParticipants:          (eventName)                => api.get(`${API_BASE}/qr-event-participant`, { eventName }),

    /** 행사 참가자 페이징 조회
     * @param {string} eventName - 행사명
     * @param {number} page - 페이지 번호
     * @param {number} size - 페이지 크기
     * @param {string} [keyword] - 검색어(이름·이메일·소속·연락처·직함). 서버측 검색이라 전체 참가자에 걸쳐 매칭된다.
     */
    getParticipantsPaginated: (eventName, page, size, keyword = "")    =>
        api.get(`${API_BASE}/qr-event-participant/paginated`, {
            eventName, page, size,
            ...(keyword && keyword.trim() ? { keyword: keyword.trim() } : {}),
        }),

    /** 특정 참가자 조회
     * @param {string} eventName - 행사명
     * @param {string} email - 참가자 이메일
     */
    getParticipant:           (eventName, email)         =>
        api.get(`${API_BASE}/qr-event-participant/target`, { eventName, email }),

    /** 참가자 등록
     * @param {object} dto - 참가자 등록 데이터
     */
    registerParticipant: (dto)         => api.post(`${API_BASE}/qr-event-participant`, dto),

    /** 참가자 정보 수정
     * @param {object} dto - 수정할 참가자 데이터
     */
    updateParticipant:   (dto)         => api.put(`${API_BASE}/qr-event-participant`, dto),

    /** 참가자 삭제 — 출석 기록도 함께 삭제된다
     * @param {number} id - 참가자 id (개인코드는 행사마다 겹칠 수 있어 id 로 지운다)
     */
    deleteParticipant:   (id) =>
        api.delete(`${API_BASE}/qr-event-participant/${id}`),

    /** 참가자 선택 삭제 — 요청한 행사의 참가자만 삭제되고, 출석 기록도 함께 삭제된다
     * @param {string} eventName - 행사명
     * @param {number[]} ids - 참가자 id 목록
     */
    bulkDeleteParticipants: (eventName, ids) =>
        api.post(`${API_BASE}/qr-event-participant/bulk-delete`, { eventName, ids }),

    /** 참가자 출석 처리
     * @param {string} eventName - 행사명
     * @param {string} privateCode - 참가자 고유 코드
     */
    attendParticipant: (eventName, privateCode) =>
        api.post(`${API_BASE}/qr-event-participant/attend`, {
            event_name: eventName,
            participant_private_code: privateCode,
        }),

    /** 참가자 목록 엑셀 다운로드
     * @param {string} eventName - 행사명
     */
    downloadParticipants: (eventName) =>
        api.download(`${API_BASE}/qr-event-participant/download`, { eventName }),

    /** 참가자 명단 엑셀 업로드 (multipart/form-data)
     * @param {string} eventName - 행사명
     * @param {File} file - 업로드할 xlsx 파일
     */
    uploadParticipants: (eventName, file) => {
        const form = new FormData();
        form.append("file", file);
        form.append("eventName", eventName);
        return api.upload(`${API_BASE}/qr-event-participant/upload`, form);
    },

    // 입출입(부스) 확인 (admin)
    /** 행사의 구역 목록 — 출석 기록에 찍힌 구역 + 구역별 방문자 수
     * @param {string} eventName - 행사명
     */
    getAttendanceSections: (eventName) =>
        api.get(`${API_BASE}/qr-event-attendance/sections`, { eventName }),

    /** 입출입 기록 페이징 조회 (방문 1건 = 1행, 이름 → 입장 시간 순)
     * @param {string} eventName - 행사명
     * @param {{ keyword?: string, sections?: string[], page: number, size: number }} query
     *   sections 가 비면 전체, 여러 개면 그중 한 곳이라도 간 기록
     */
    getAttendanceRecords: (eventName, { keyword = "", sections = [], page, size }) =>
        api.get(`${API_BASE}/qr-event-attendance`, {
            eventName, page, size,
            ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
            ...(sections.length ? { sections } : {}),
        }, {
            // 배열을 sections=A&sections=B 로 보낸다 — axios 기본값(sections[]=A)은 Spring 이 받지 못한다.
            paramsSerializer: { indexes: null },
        }),

    /** 입출입 기록 엑셀 다운로드 — 화면에 걸린 조건(검색어·구역)의 기록 전체
     * @param {string} eventName - 행사명
     * @param {{ keyword?: string, sections?: string[] }} query
     */
    downloadAttendanceRecords: (eventName, { keyword = "", sections = [] }) =>
        api.download(`${API_BASE}/qr-event-attendance/download`, {
            eventName,
            ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
            ...(sections.length ? { sections } : {}),
        }, {
            paramsSerializer: { indexes: null },
        }),

    // 공개 (public — 로그인 불필요)
    /** 공개 참가자 조회 (로그인 불필요)
     * @param {string} eventName - 행사명
     * @param {string} email - 참가자 이메일
     */
    publicGetParticipant: (eventName, email) =>
        api.get("/public/qr-event-participant/target", { eventName, email }),

    /** 공개 참가 등록 (로그인 불필요)
     * @param {object} dto - 참가 등록 데이터
     */
    publicRegister: (dto)          => api.post("/public/qr-event-participant/register", dto),

    /** QR 명함 조회 (로그인 불필요)
     * @param {string} privateCode - 참가자 고유 코드
     */
    getBizCard:     (privateCode)  => api.post("/public/qr-event-participant/bizcard", { privateCode }),
};
